import { CSSProperties } from 'react';
import { useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import CloseIcon from '@/icons/Cross.svg';
import { createBEMClasses } from '@/utils/classname';
import { useWebkitVirtualKeyboardHeight } from '@/utils/useWebkitVirtualKeyboardHeight';

import './PageLayout.css';

const { block, element } = createBEMClasses('page-layout');

export const PageLayout = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const virtualKeyboardHeight = useWebkitVirtualKeyboardHeight();

  return (
    <Stack
      className={block({}, className)}
      style={
        {
          '--virtual-keyboard-height': `${virtualKeyboardHeight}px`,
        } as CSSProperties
      }
    >
      {children}
    </Stack>
  );
};

export const PageLayoutHeader = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <Stack className={element('header', {}, className)}>{children}</Stack>;
};

export const PageLayoutSimpleHeader = ({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
}) => {
  const { t } = useLingui();

  return (
    <PageLayoutHeader className="simple-header">
      <Stack>
        <Title size="200" as="h1">
          {title}
        </Title>
        {subtitle && <Paragraph weight="medium">{subtitle}</Paragraph>}
      </Stack>
      <Button
        onPress={onClose}
        ariaLabel={t({ id: 'full-page-layout.close', message: 'Close' })}
        iconOnly
        size="medium"
        variant="tertiary"
      >
        <CloseIcon />
      </Button>
    </PageLayoutHeader>
  );
};

export const PageLayoutContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack row flexWrap="nowrap" className={element('content')}>
      {children}
    </Stack>
  );
};

export const PageLayoutLeftPart = ({ children }: { children: React.ReactNode }) => {
  return <Stack className={element('left-part')}>{children}</Stack>;
};

export const PageLayoutLeftPartContent = ({ children }: { children: React.ReactNode }) => {
  return <Stack className={element('left-part-content')}>{children}</Stack>;
};

export const PageLayoutRightPart = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: CSSProperties;
}) => {
  return (
    <Stack className={element('right-part')} style={style}>
      {children}
    </Stack>
  );
};

export const PageLayoutTopPart = ({
  children,
  ref,
}: {
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <Stack className={element('top-part')} ref={ref}>
      {children}
    </Stack>
  );
};

export const PageLayoutCenter = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      style={{ height: '100%' }}
      gap="1rem"
      alignItems="center"
      flexWrap="nowrap"
      justifyContent="center"
    >
      {children}
    </Stack>
  );
};
